/**
 * Leviathan - User Detail Page
 * Quasar Prime LLC
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { API_URL } from '@/config/api';
import { parseApiError } from '@/utils/errorHandler';
import { isAdminRole, roleLabel } from '@/constants/userRoles';

interface Role {
  id: string;
  name: string;
  permissions: string[];
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  isActive: boolean;
  lastLoginAt: string | null;
  loginCount: number;
  createdAt: string;
  updatedAt: string;
  /** UserRole enum — the access level the API enforces. */
  role: string;
  /** Named permission group. Additive: it can only ADD to the role. */
  roleGroup: Role | null;
  customPermissions: string[];
}

export default function UserDetail() {
  const { id } = useParams<{ id: string }>();
  const { accessToken, user: currentUser } = useAuth();
  const isAdmin = isAdminRole(currentUser?.role);
  const isSelf = id === currentUser?.id;

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchUser();
  }, [id]);

  const fetchUser = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to fetch user');
      const data = await response.json();
      setUser(data.user);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (isActive: boolean) => {
    if (!user) return;
    try {
      const response = await fetch(`${API_URL}/api/users/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ isActive }),
      });
      // Surface the server's own reason. It refuses some of these deliberately
      // — the last administrator, your own account — and "Failed to update
      // user" would hide the one sentence that explains why.
      if (!response.ok) throw new Error(await parseApiError(response));
      const data = await response.json();
      setUser(data.user);
      setShowDeactivateModal(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user');
      setShowDeactivateModal(false);
    }
  };

  const resetPassword = async () => {
    setResetting(true);
    try {
      const response = await fetch(`${API_URL}/api/users/${id}/reset-password`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to reset password');
      const data = await response.json();
      setTempPassword(data.tempPassword);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
      setShowResetModal(false);
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error || 'User not found'}
        </div>
        <Link to="/admin/users" className="text-blue-600 hover:underline mt-4 inline-block">
          &larr; Back to Users
        </Link>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Link to="/admin/users" className="text-blue-600 hover:underline text-sm">
            &larr; Back to Users
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 mt-2">
            {user.firstName} {user.lastName}
          </h1>
          <p className="text-gray-500">{user.email}</p>
        </div>
        {/* Admin-only, mirroring `requireAdmin` on every one of these routes.
            A non-admin sees the record and none of the controls. */}
        {isAdmin && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => (user.isActive ? setShowDeactivateModal(true) : toggleActive(true))}
              disabled={isSelf && user.isActive}
              title={isSelf && user.isActive ? 'You cannot deactivate your own account' : undefined}
              className={`px-4 py-2 rounded-lg font-medium disabled:opacity-40 disabled:cursor-not-allowed ${
                user.isActive
                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              {user.isActive ? 'Deactivate' : 'Reactivate'}
            </button>
            <button
              onClick={() => setShowResetModal(true)}
              className="px-4 py-2 bg-amber-100 text-amber-700 rounded-lg font-medium hover:bg-amber-200"
            >
              Reset Password
            </button>
            <Link
              to={`/admin/users/${id}/permissions`}
              className="px-4 py-2 bg-slate-200 text-slate-800 rounded-lg font-medium hover:bg-slate-300"
            >
              Permissions
            </Link>
            <Link
              to={`/admin/users/${id}/edit`}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
            >
              Edit User
            </Link>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          label="Status"
          value={user.isActive ? 'Active' : 'Inactive'}
          color={user.isActive ? 'green' : 'red'}
        />
        <StatCard label="Access Level" value={roleLabel(user.role)} />
        <StatCard label="Login Count" value={user.loginCount.toString()} />
        <StatCard
          label="Last Login"
          value={user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleDateString() : 'Never'}
        />
      </div>

      {/* User Information */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel title="User Information">
          <div className="space-y-3">
            <InfoRow label="First Name" value={user.firstName} />
            <InfoRow label="Last Name" value={user.lastName} />
            <InfoRow label="Email" value={user.email} />
            <InfoRow label="Phone" value={user.phone || '-'} />
            <InfoRow label="Created" value={new Date(user.createdAt).toLocaleString()} />
            <InfoRow label="Updated" value={new Date(user.updatedAt).toLocaleString()} />
          </div>
        </Panel>

        <Panel title="Role & Permissions">
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-1">Access Level (enforced)</h4>
              <p className="text-gray-900">{roleLabel(user.role)}</p>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-1">Permission Group</h4>
              <p className="text-gray-900">{user.roleGroup?.name || 'None assigned'}</p>
            </div>

            {/* This panel used to compute an "effective permissions" list here,
                from the permission group plus `customPermissions`. That answer
                is not the one the server reaches — it misses the role's own
                defaults and every per-user override — so showing it would be a
                confident, wrong statement about someone's access. The authority
                is the API, and the screen that asks it is one click away. */}
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-2">Effective Permissions</h4>
              <p className="text-sm text-gray-600 mb-3">
                Resolved by the server from the access level, the permission group, and any
                decisions made about this person specifically.
              </p>
              <Link
                to={`/admin/users/${id}/permissions`}
                className="inline-block px-4 py-2 border border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 text-sm"
              >
                View and edit permissions &rarr;
              </Link>
            </div>
          </div>
        </Panel>
      </div>

      {/* Deactivate Confirmation. Deactivation is how a customer revokes access
          when they fire someone, so it gets a real confirmation step — and says
          plainly that it is not a deletion, because the alternative reading is
          that their invoices are about to disappear. */}
      {showDeactivateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Deactivate User</h3>
            <p className="text-gray-600 mb-3">
              Deactivate{' '}
              <strong>
                {user.firstName} {user.lastName}
              </strong>{' '}
              ({user.email})?
            </p>
            <ul className="text-sm text-gray-600 space-y-1 mb-6 list-disc list-inside">
              <li>They can no longer sign in, and an existing session cannot be renewed.</li>
              <li>Everything they created is left exactly as it is. This is not a deletion.</li>
              <li>You can reactivate them from this page at any time.</li>
            </ul>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeactivateModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => toggleActive(false)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            {tempPassword ? (
              <>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Password Reset Complete</h3>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-600 mb-2">New temporary password:</p>
                  <code className="text-lg font-mono font-bold text-green-700">{tempPassword}</code>
                </div>
                <p className="text-sm text-gray-500 mb-4">
                  Please provide this password to the user. They will be required to change it on
                  next login.
                </p>
                <button
                  onClick={() => {
                    setShowResetModal(false);
                    setTempPassword(null);
                  }}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Done
                </button>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Reset Password</h3>
                <p className="text-gray-600 mb-4">
                  Are you sure you want to reset the password for {user.firstName} {user.lastName}?
                  This will generate a new temporary password.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowResetModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    disabled={resetting}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={resetPassword}
                    className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                    disabled={resetting}
                  >
                    {resetting ? 'Resetting...' : 'Reset Password'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Helper Components
function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: 'green' | 'red';
}) {
  const colorClasses = {
    green: 'text-green-600',
    red: 'text-red-600',
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <p className="text-sm text-gray-500">{label}</p>
      <p className={`text-xl font-semibold ${color ? colorClasses[color] : 'text-gray-900'}`}>
        {value}
      </p>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      {children}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between py-2 border-b border-gray-100 last:border-0">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-900 font-medium">{value}</span>
    </div>
  );
}
